<template>
  <div>
    <div v-if="loading" class="flex justify-center items-center min-h-[200px]">
      <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-custom-yellow"></div>
    </div>
    <template v-else>
      <div class="relative overflow-x-auto shadow-md sm:rounded-lg mt-5">
        <table class="w-full text-left rtl:text-right text-gray-700">
          <thead>
            <tr class="bg-gray-100">
              <th scope="col" class="p-4">
                <div class="flex items-center">
                  <input id="checkbox-all-search" type="checkbox"
                    class="w-4 h-4 text-custom-yellow bg-gray-100 border-gray-300 rounded focus:ring-custom-yellow dark:focus:ring-custom-yellow dark:ring-offset-gray-800 dark:focus:ring-offset-gray-800 focus:ring-2 dark:bg-gray-700 dark:border-gray-600" />
                  <label for="checkbox-all-search" class="sr-only">checkbox</label>
                </div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Test Name</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Date Taken</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Exerminer</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Score</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Percentage</div>
              </th>
              <th scope="col" class="px-6">
                <div class="flex items-center">Status</div>
              </th>
              <th scope="col" class="px-6">Action</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="(result, index) in filteredResults" :key="index" class="border-b">
              <td class="w-4 p-4">
                <div class="flex items-center">
                  <input :id="'checkbox-table-search-' + index" type="checkbox" class="w-4 h-4" />
                  <label :for="'checkbox-table-search-' + index" class="sr-only">checkbox</label>
                </div>
              </td>
              <td class="px-6 py-1">{{ result.assessment?.assessment_name }}</td>
              <td class="px-6 py-1">
                {{
                  new Date(result.examination_date).toLocaleString()
                }}
              </td>
              <td class="px-6 py-1">{{ result.assessment?.creator?.first_name }}
                {{ result.assessment?.creator?.last_name }}
              </td>
              <td class="px-6 py-1">
                {{ result.score }}/{{ result.assessment?.total_questions }}
              </td>
              <td class="px-6 py-2">
                {{ (result.score / result.assessment?.total_questions) * 100 }}%
              </td>
              <td class="px-6 py-2">
                <span :class="isAssessmentPassed(result) ? 'text-green-600 font-bold' : 'text-red-600 font-bold'">
                  {{ isAssessmentPassed(result) ? 'PASS' : 'FAIL' }}
                </span>
              </td>
              <td class="flex items-center px-4 py-2 space-x-2">
                <CustomButton v-if="!isAssessmentPassed(result)" label="Retake" bg-class="bg-red-500 text-white text-sm py-1"
                  @click="handleRetakeAssessment(result.assessment?.assessment_id)" />
                <CustomButton label="Details" bg-class="bg-custom-yellow text-sm py-1"
                  @click="handleReviewAssesment(result.assessment?.assessment_id)" />
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <div class="flex justify-center gap-4 mt-4">
        <button @click="previousPage" :disabled="currentPage === 1"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m15 19-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div class="flex items-center gap-2">
          <button v-for="page in visiblePages" :key="page" @click="goToPage(page)" :class="{
            'bg-custom-blue text-white': page === currentPage,
            'text-custom-blue': page !== currentPage
          }"
            class="relative h-10 max-h-[40px] w-10 max-w-[40px] select-none rounded-lg text-center align-middle font-medium transition-all hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
            type="button">
            <span class="absolute transform -translate-x-1/2 -translate-y-1/2 top-1/2 left-1/2">
              {{ page }}
            </span>
          </button>
        </div>

        <button @click="nextPage" :disabled="currentPage === totalPages"
          class="flex items-center gap-2 px-6 py-3 font-bold text-center text-custom-blue align-middle transition-all rounded-lg select-none hover:bg-custom-blue/10 active:bg-custom-blue/20 disabled:pointer-events-none disabled:opacity-50 disabled:shadow-none"
          type="button">
          Next
          <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"
            viewBox="0 0 24 24">
            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
              d="m9 5 7 7-7 7" />
          </svg>
        </button>
      </div>
    </template>

    <!-- Retake Test Modal -->
    <div v-if="showCatModal"
      class="fixed inset-0 bg-gray-800 bg-opacity-70 overflow-y-auto h-full w-full flex items-center justify-center">
      <div class="bg-white p-8 rounded-lg shadow-xl w-4/10 mt-4 mb-4">
        <h2 class="text-2xl text-gray-700 font-bold mb-4">Retake Test</h2>
        <hr class="h-px my-4 bg-orange-200 border-0 dark:bg-orange-200">
        <div class="mb-6">
          <div class="py-2 px-4 mb-4 border-l-4 text-custom-yellow border-custom-yellow dark:bg-orange-50 rounded-sm"
            role="alert">
            <div class="flex items-center">
              <svg class="w-6 h-6" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24" height="24"
                fill="none" viewBox="0 0 24 24">
                <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                  d="M10 11h2v5m-2 0h4m-2.592-8.5h.01M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
              </svg>
              <span class="sr-only">warning</span>
              <h3 class="text-lg px-2 font-medium text-gray-800">Reminder!</h3>
            </div>
            <div class="mb-3 px-8 text-sm text-gray-800">
              Kindly make sure you have stable internet and power before starting the test.<br>
              Kindly note that once you start a test, the timer starts running for the allocated period of time.<br>
              Kindly note that once the allocated duration of time elapses, your assessment will be automatically
              submitted
            </div>
          </div>
          <hr class="h-px my-4 bg-orange-200 border-0 dark:bg-orange-200">
        </div>
        <div class="flex justify-between space-x-2">
          <button type="button" @click="closeTakeCatModal"
            class="px-4 py-2 bg-gray-200 text-gray-800 rounded">Cancel</button>
          <button @click="handleStartTest"
            class="px-4 py-2 bg-custom-yellow text-white rounded">Start Now</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CustomButton from "@/components/CustomButton.vue";
import api from "@/services/api";
import SweetAlertService from "@/services/SweetAlertService";

export default {
  name: "ResultsTab",
  components: {
    CustomButton,
  },
  created() {
    this.fetchAssessmentResults();
  },
  props: {
    searchQuery: {
      type: String,
      default: "",
    },
  },
  data() {
    return {
      results: [],
      error: null,
      currentPage: 1,
      totalPages: 0,
      perPage: 10,
      loading: false,
      showCatModal: false,
      isRetakeAttempt: false,
      retakeAssessmentId: null,
    };
  },
  computed: {
    userClaims() {
      return this.$store.getters["auth/claims"];
    },
    userData() {
      return this.$store.getters["auth/user"];
    },

    visiblePages() {
      return this.calculatePageRange();
    },

    filteredResults() {
      const search = this.searchQuery.toLowerCase();
      return this.results?.exam_results.filter((result) => {
        const assessmentName = result.assessment?.assessment_name || '';
        const examDate = result.examination_date || '';
        const examinerFirstName = result.assessment?.creator?.first_name || '';
        const examinerLastName = result.assessment?.creator?.last_name || '';

        const matchesSearch =
          assessmentName.toLowerCase().includes(search) ||
          examinerFirstName.toLowerCase().includes(search) ||
          examinerLastName.toLowerCase().includes(search) ||
          examDate.toLowerCase().includes(search);

        return matchesSearch;
      });
    }
  },

  watch: {
    searchQuery: 'fetchAssessmentResults'
  },

  methods: {
    async fetchAssessmentResults() {
      this.loading = true;
      try {
        const queryParams = { search_query: this.searchQuery, page: this.currentPage, per_page: this.perPage };
        const response = await api.getUserAssessmentResults(queryParams);
        this.results = response.data || response;
        this.currentPage = response.current_page;
        this.totalPages = response.total_pages;
      } catch (error) {
        this.error = error.message || "Failed to fetch results";
      } finally {
        this.loading = false;
      }
    },

    isAssessmentPassed(result) {
      // Check if we have the necessary data
      if (!result.assessment || !result.score || !result.assessment.total_questions) {
        return false;
      }
      
      // Use passmark from assessment if available, otherwise default to 50%
      const passmark = result.assessment.passmark || 50;
      const percentage = (result.score / result.assessment.total_questions) * 100;
      
      return percentage >= passmark;
    },

    async handleRetakeAssessment(assessment_id) {
      try {
        await api.createRetakeExam(assessment_id);
        
        // Store the assessment ID and show the reminder modal
        this.retakeAssessmentId = assessment_id;
        this.isRetakeAttempt = true;
        this.showCatModal = true;
      } catch (error) {
        console.error('Failed to start retake:', error);
        
        if (error.message && error.message.includes('already passed') ||
            error.errors && error.errors.some(err => err.includes('already passed'))) {
          this.$router.push({
            name: 'AssessmentResultPage',
            params: { assessment_id: assessment_id }
          });
        } else {
          SweetAlertService.showError('Error', error.message || 'Failed to start retake assessment');
        }
      }
    },

    handleReviewAssesment(assessment_id) {
      this.$router.push({ name: 'ExamTestReviewPage', params: { assessment_id: assessment_id } });
    },

    closeTakeCatModal() {
      this.showCatModal = false;
      this.isRetakeAttempt = false;
      this.retakeAssessmentId = null;
    },

    handleStartTest() {
      // Store retake status before closing modal
      const isRetake = this.isRetakeAttempt;
      const assessmentId = this.retakeAssessmentId;
      
      // Close modal first
      this.showCatModal = false;
      this.isRetakeAttempt = false;
      this.retakeAssessmentId = null;
      
      // Navigate to test page with retake parameter if it's a retake
      if (isRetake && assessmentId) {
        this.$router.push({ 
          name: 'StaffAssessmentTestPage', 
          params: { assessment_id: assessmentId },
          query: { retake: 'true' }
        });
      }
    },

    calculatePageRange() {
      const maxPagesToShow = 5;
      let startPage = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
      let endPage = Math.min(this.totalPages, startPage + maxPagesToShow - 1);

      // Adjust start page if we're near the end
      if (endPage - startPage + 1 < maxPagesToShow) {
        startPage = Math.max(1, endPage - maxPagesToShow + 1);
      }

      return Array.from(
        { length: endPage - startPage + 1 },
        (_, i) => startPage + i
      );
    },

    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        this.fetchAssessmentResults();
      }
    },
    async previousPage() {
      if (this.currentPage > 1) {
        this.currentPage--;
        await this.fetchAssessmentResults();
      }
    },
    async nextPage() {
      if (this.currentPage < this.totalPages) {
        this.currentPage++;
        await this.fetchAssessmentResults();
      }
    },

  },
};
</script>

<style lang="css" scoped></style>
